import { useState, useEffect } from 'react';
import { pendingInvites, acceptInvite, declineInvite } from '../api/invites';
import { getCurrentOrg } from '../api/orgs';
import './InviteModal.css';
import Button from './Button';

// Blocking organization-invitation prompt. Mounted app-wide in App.js so it
// overlays every authenticated page (a newly-invited user can't miss it on
// Dashboard/Settings/Team). No close/cancel path — the only way past it is to
// Accept or Decline each pending invite, one at a time, until the queue drains.
export default function InviteModal({ onOrgUpdate }) {
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    pendingInvites()
      .then((r) => setQueue(r.invites || []))
      .catch(() => {});
  }, []);

  if (queue.length === 0) return null;
  const inv = queue[0];

  function dropCurrent() {
    setError(null);
    setQueue((prev) => prev.filter((i) => i.inviteId !== inv.inviteId));
  }

  async function handleAccept() {
    setBusy(true);
    try {
      await acceptInvite(inv.orgId, inv.inviteId);
      try {
        onOrgUpdate?.(await getCurrentOrg());
      } catch (e) {
        /* best-effort */
      }
      dropCurrent();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleDecline() {
    setBusy(true);
    try {
      await declineInvite(inv.orgId, inv.inviteId);
    } catch (e) {
      /* best-effort */
    }
    dropCurrent();
    setBusy(false);
  }

  return (
    <div className="invite-modal-overlay">
      <div className="invite-modal">
        <h2>You've been invited</h2>
        <p className="invite-modal-text">
          You've been invited to <strong>{inv.orgName || 'a team'}</strong>
          {inv.role ? ` as ${inv.role}` : ''}.
        </p>
        {queue.length > 1 && (
          <p className="invite-modal-count">{queue.length} pending invitations</p>
        )}
        {error && <p className="invite-modal-error">{error}</p>}
        <div className="invite-modal-actions">
          <Button variant="primary" onClick={handleAccept} disabled={busy}>
            {busy ? 'Working…' : 'Accept'}
          </Button>
          <Button variant="secondary" onClick={handleDecline} disabled={busy}>
            Decline
          </Button>
        </div>
      </div>
    </div>
  );
}
